import { useState } from "react";
import { Stack, Typography, InputAdornment, IconButton, Drawer, TextField } from "~/common/mui";
import { QuantityStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useVariantContext } from "~/features/productDetails/context/VariantContext";
import CheckIcon from "@mui/icons-material/Check";
import Tag from "~/common/components/Tag/Tag";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { DrawerContent, DrawerHeader } from "~/common/components/Drawer/Drawer";
import { verticalStacker } from "~/styles/styledMixins";
import { VariantMenuItem } from "./VariantSelect";
import type { variantGroupProductDTO } from "~/features/productDetails/dto/PdpPageProductDataDto";

export const MobileVariantDrawer = () => {
  const { variantGroups, onSelectVariant, variantLoading } = useVariantContext();

  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    group: null | variantGroupProductDTO;
  }>({
    open: false,
    group: null,
  });

  const isBusinessUser = userIsCustomer();

  if (!variantGroups || variantGroups.length === 0) {
    return null;
  }

  const onClose = () => {
    setDrawerState({ open: false, group: null });
  };

  return (
    <Stack gap={2}>
      {variantGroups.map((group) => {
        const selectedOption = group.options.find((o) => o.selected);
        return (
          <Stack key={group.name} gap={1}>
            <Typography variant="labelMedium">{group.name}</Typography>

            <TextField
              value={selectedOption?.value ?? ""}
              onClick={() => setDrawerState({ open: true, group })}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <ExpandMoreIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Drawer anchor="bottom" open={drawerState.open && drawerState.group?.name === group.name} onClose={onClose}>
              <DrawerContent sx={{ ...verticalStacker("24px") }}>
                <DrawerHeader>
                  <Typography variant="titleLarge" component={"b"}>
                    {group.name}
                  </Typography>
                  <IconButton variant="contained" color={"secondary"} size={"tiny"} onClick={onClose}>
                    <CloseRoundedIcon />
                  </IconButton>
                </DrawerHeader>

                <Stack gap={1}>
                  {group.options.map((option) => {
                    return (
                      <VariantMenuItem
                        key={option.url}
                        selected={option.selected}
                        onClick={() => {
                          onSelectVariant(option);
                          onClose();
                        }}
                        disabled={variantLoading}
                        component="a"
                        href={option.url}
                      >
                        <Stack direction="row" gap={1.5}>
                          <CheckIcon
                            sx={{ visibility: option.selected ? "visible" : "hidden" }}
                            color={"onSecondaryContainer"}
                          />
                          <Typography variant="bodyLarge">{option.value}</Typography>
                        </Stack>

                        {isBusinessUser && (
                          <Tag variant={option.stock ? "primary" : "default"}>
                            <QuantityStockIndicator quantity={option.stock} />
                          </Tag>
                        )}
                      </VariantMenuItem>
                    );
                  })}
                </Stack>
              </DrawerContent>
            </Drawer>
          </Stack>
        );
      })}
    </Stack>
  );
};
